import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';

import {
  FilterListOutlined,
  SearchOutlined
} from '@mui/icons-material';

import CommonTable from '../../../../Common/components/Listing/CommonTable';

import {
  filterButtonSx,
  patientCountChipSx,
  providerHeaderSx,
  providerNameSx,
  toolbarSx
} from '../../../../Common/components/Styles/InputStyles';

import {
  columns,
  patientRows
} from './data';

import {
  detailsCardContentSx,
  detailsCardSx,
  detailsHeaderWrapperSx,
  detailsSearchFieldSx,
  searchIconSx
} from './DetailsStyles';

const Details = ({ className, ...rest }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const rows = useMemo(
    () =>
      patientRows.map((row, index) => ({
        id: index + 1,
        pname: row[0],
        dob: row[1],
        mr: row[2],
        pri: row[3],
        seco: row[4],
        phn: row[5]
      })),
    []
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = search
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter(row =>
      Object.values(row).some(value =>
        String(value)
          .toLowerCase()
          .includes(normalizedSearch)
      )
    );
  }, [rows, search]);

  const visibleRows = useMemo(() => {
    const startIndex = page * rowsPerPage;

    return filteredRows.slice(
      startIndex,
      startIndex + rowsPerPage
    );
  }, [filteredRows, page, rowsPerPage]);

  const handleSearchChange = event => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  return (
    <Card
      {...rest}
      className={className}
      sx={detailsCardSx}>
      <Box sx={detailsHeaderWrapperSx}>
        <Box sx={providerHeaderSx}>
          <Typography sx={providerNameSx}>
            Dr. Charles
          </Typography>

          <Chip
            label={`${filteredRows.length} Patients`}
            size="small"
            sx={patientCountChipSx}
          />
        </Box>

        <Box sx={toolbarSx}>
          <TextField
            size="small"
            placeholder="Search patients..."
            value={search}
            onChange={handleSearchChange}
            sx={detailsSearchFieldSx}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined sx={searchIconSx} />
                  </InputAdornment>
                )
              }
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterListOutlined />}
            sx={filterButtonSx}>
            Filter
          </Button>
        </Box>
      </Box>

      <CardContent sx={detailsCardContentSx}>
        <CommonTable
          columns={columns}
          rows={visibleRows}
          totalCount={filteredRows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 75, 100]}
          emptyText="No patients found."
          loading={loading}
          skeletonRows={25}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </CardContent>
    </Card>
  );
};

Details.propTypes = {
  className: PropTypes.string
};

export default Details;